import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ErrorBox, Loading } from '../components/Feedback';
import { TicketHeader } from '../components/TicketHeader';
import { api } from '../lib/api';
import type { Ticket } from '../types';

const PAGE_SIZE = 10;

function dateLabel(value?: string | null): string {
  if (!value) return 'Dată necunoscută';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Dată necunoscută';
  return new Intl.DateTimeFormat('ro-RO', {
    timeZone: 'Europe/Bucharest', day: '2-digit', month: 'short', year: 'numeric',
  }).format(date);
}

function statusLabel(ticket: Ticket): string {
  const explicit = String(ticket.statusLabel || '').trim();
  if (explicit) return explicit;
  const status = String(ticket.apiStatus || ticket.ticketStatus || '');
  return ({
    '1': 'Nou', '8': 'Alocat în service', '10': 'În lucru service', '9': 'Revizionat',
    '11': 'Alocat în teren', '7': 'În lucru teren', '12': 'De validat', '6': 'Finalizat',
  } as Record<string, string>)[status] || 'Tichet';
}

function activityNames(ticket: Ticket): string[] {
  const source = ticket.activities || ticket.interventionReport?.activities || [];
  return source.map((activity) => String(activity?.name || '').trim()).filter(Boolean);
}

export function EquipmentHistoryPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const runId = params.get('runId') || '';
  const [current, setCurrent] = useState<Ticket>();
  const [rows, setRows] = useState<Ticket[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [ticket, history] = await Promise.all([
        api.ticket(id, runId),
        api.ticketHistory(id, page, PAGE_SIZE),
      ]);
      setCurrent(ticket);
      setRows(history.items || history.rows || []);
      setTotal(history.total || 0);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Istoricul aparatului nu poate fi încărcat.');
    } finally {
      setLoading(false);
    }
  }, [id, page, runId]);

  useEffect(() => { void load(); }, [load]);

  if (!current && loading) return <Loading />;
  if (!current) return <div className="page-body"><ErrorBox message={error || 'Tichetul nu poate fi încărcat.'} /></div>;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return <div className="page equipment-history-page">
    <TicketHeader ticket={current} title="Istoric aparat" />
    <section className="page-body">
      <article className="card equipment-history-summary">
        <span className="eyebrow">Istoric probleme aparat</span>
        <h2>{current.deviceName || 'Aparat fără denumire'}</h2>
        <p className="muted">Probleme și remedieri înregistrate anterior pentru acest aparat.</p>
      </article>
      {error && <ErrorBox message={error} />}
      {loading && <Loading />}
      {!loading && !rows.length && <div className="empty"><strong>Nu există tichete anterioare pentru acest aparat.</strong></div>}
      {!loading && rows.length > 0 && <div className="equipment-history-list">{rows.map((ticket) => {
        const key = ticket.ticketId || ticket._id;
        const open = expanded === key;
        const activities = activityNames(ticket);
        return <article className="card equipment-history-item" key={key}>
          <button type="button" className="equipment-history-item__toggle" aria-expanded={open} onClick={() => setExpanded(open ? '' : key)}>
            <span className="equipment-history-item__meta">
              <small>{dateLabel(ticket.createdAt)}</small>
              <span>{statusLabel(ticket)}</span>
            </span>
            <span className="equipment-history-item__title">Tichet #{ticket.ticketNumber || '—'}</span>
            <span className="equipment-history-item__description">{ticket.description || 'Fără descriere'}</span>
            <span className="equipment-history-item__chevron">{open ? '−' : '+'}</span>
          </button>
          {open && <div className="equipment-history-item__details">
            <div><span className="eyebrow">Problema raportată</span><p>{ticket.description || 'Nu există descriere.'}</p></div>
            <div><span className="eyebrow">Soluția aplicată</span><p>{ticket.solution || 'Nu există soluție înregistrată.'}</p></div>
            <div><span className="eyebrow">Activități efectuate</span>{activities.length
              ? <ul>{activities.map((activity, index) => <li key={`${key}:${index}`}>{activity}</li>)}</ul>
              : <p>Nu există activități înregistrate.</p>}</div>
          </div>}
        </article>;
      })}</div>}
      {!loading && total > PAGE_SIZE && <nav className="equipment-history-pagination" aria-label="Paginare istoric aparat">
        <button className="btn btn-secondary" type="button" disabled={page <= 1} onClick={() => { setExpanded(''); setPage((value) => Math.max(1, value - 1)); }}>Anterior</button>
        <span>Pagina {page} din {pages}</span>
        <button className="btn btn-secondary" type="button" disabled={page >= pages} onClick={() => { setExpanded(''); setPage((value) => Math.min(pages, value + 1)); }}>Următor</button>
      </nav>}
    </section>
  </div>;
}
